import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronRight, ChevronDown } from 'lucide-react';
const tomos = [{
  icon: '📘',
  title: 'Tomo I – Protocolo de Entrevista Inicial y Admisión',
  description: 'Plantillas listas para documentar cada consulta de forma clara, completa y profesional en minutos.',
  items: ['Historia clínica psiquiátrica inicial completa', 'Evaluación del Estado Mental (EEM) estructurada', 'Ficha de antecedentes personales, familiares y psiquiátricos', 'Registro de motivo de consulta y expectativas del paciente', 'Plantilla de resumen clínico para derivaciones y contrarreferencias'],
  color: 'from-blue-900/40 to-navy'
}, {
  icon: '📗',
  title: 'Tomo II – Vademécum de Evolución y Seguimiento',
  description: 'Registro profesional de cada sesión para un seguimiento clínico preciso y sin fisuras.',
  items: ['Hoja de evolución sesión por sesión', 'Registro de cambios en el estado clínico y funcional', 'Planilla de objetivos terapéuticos y avances', 'Control de adherencia al tratamiento', 'Seguimiento de síntomas por trastorno (estado de ánimo, ansiedad, psicosis)'],
  color: 'from-emerald-900/40 to-navy'
}, {
  icon: '📕',
  title: 'Tomo III – El Escudo Legal del Psiquiatra',
  description: 'Protegé tu matrícula y tu práctica con documentación legal sólida y lista para firmar.',
  items: ['Consentimiento informado para tratamiento psiquiátrico', 'Acuerdo de confidencialidad y límites del secreto profesional', 'Protocolo de alta voluntaria y abandono de tratamiento', 'Registro de decisiones clínicas de riesgo', 'Plantilla de denuncia obligatoria ante situaciones de riesgo'],
  color: 'from-red-900/40 to-navy'
}, {
  icon: '📙',
  title: 'Tomo IV – Planificador de Farmacoterapia Avanzada',
  description: 'El control total sobre la medicación de cada paciente en un solo lugar.',
  items: ['Registro de prescripción, dosis y ajustes farmacológicos', 'Control de efectos adversos y tolerancia', 'Seguimiento de laboratorio asociado a medicación', 'Checklist de interacciones medicamentosas críticas', 'Planilla de revisión de polifarmacia'],
  color: 'from-orange-900/40 to-navy'
}, {
  icon: '📓',
  title: 'Tomo V – Guía de Urgencias y Crisis en Consultorio',
  description: 'Protocolos de intervención rápida para las situaciones más críticas de la práctica psiquiátrica.',
  items: ['Protocolo de evaluación de riesgo suicida (ideación, plan, intención)', 'Manejo de crisis de agitación psicomotriz', 'Intervención ante episodio psicótico agudo', 'Protocolo de contención emocional y derivación urgente', 'Registro de intervenciones de crisis y decisiones tomadas'],
  color: 'from-purple-900/40 to-navy'
}, {
  icon: '📔',
  title: 'Tomo VI – Portafolio de Informes Forenses y Legales',
  description: 'Plantillas profesionales para actuaciones fuera del consultorio.',
  items: ['Informe psiquiátrico para juzgados y organismos oficiales', 'Certificado de aptitud / no aptitud psiquiátrica', 'Informe para obras sociales y prepagas', 'Plantilla de pericia psiquiátrica simplificada', 'Registro de comunicaciones con terceros institucionales'],
  color: 'from-yellow-900/40 to-navy'
}];
export function TomosSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };
  return <section className="py-12 md:py-24 bg-navy relative overflow-hidden" data-id="element-201">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" data-id="element-202"></div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-203">
        <div className="text-center mb-8 md:mb-16" data-id="element-204">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-5xl font-serif font-bold text-white mb-3 md:mb-6" data-id="element-205">
            ¿Qué incluye el{' '}
            <span className="gold-gradient-text" data-id="element-206">Sistema PRO</span>?
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-207">
            Un sistema completo de recursos profesionales listos para usar.
            Incluye 6 tomos con plantillas, protocolos y herramientas clínicas
            que podés aplicar directamente en tu práctica psiquiátrica desde el
            primer día.
          </motion.p>
        </div>

        {/* Mobile: Accordion layout */}
        <div className="flex flex-col gap-3 md:hidden" data-id="element-208">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.05
        }} className={`glass-panel rounded-xl overflow-hidden border border-navy-lighter bg-gradient-to-br ${tomo.color} ${expandedIndex === index ? 'border-gold/40' : ''}`} data-id="element-209">
              <button onClick={() => toggleExpand(index)} className="w-full flex items-center gap-3 p-4 text-left" data-id="element-210">
                <span className="text-2xl shrink-0" data-id="element-211">{tomo.icon}</span>
                <div className="flex-grow min-w-0" data-id="element-212">
                  <h3 className="text-sm font-bold text-white leading-snug" data-id="element-213">
                    {tomo.title}
                  </h3>
                </div>
                <ChevronDown className={`w-5 h-5 text-gold shrink-0 transition-transform duration-300 ${expandedIndex === index ? 'rotate-180' : ''}`} data-id="element-214" />
              </button>

              <AnimatePresence data-id="element-215">
                {expandedIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-216">
                    <div className="px-4 pb-4 border-t border-white/10" data-id="element-217">
                      <p className="text-gray-300 text-xs mt-3 mb-3" data-id="element-218">
                        {tomo.description}
                      </p>
                      <ul className="space-y-2" data-id="element-219">
                        {tomo.items.map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-220">
                            <CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" data-id="element-221" />
                            <span className="text-gray-200 text-xs" data-id="element-222">
                              {item}
                            </span>
                          </li>)}
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Desktop: Grid layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-8" data-id="element-223">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className={`glass-panel rounded-2xl overflow-hidden border border-navy-lighter hover:border-gold/30 transition-all duration-300 bg-gradient-to-br ${tomo.color}`} data-id="element-224">
              <div className="p-8" data-id="element-225">
                <div className="flex items-start space-x-4 mb-6" data-id="element-226">
                  <span className="text-4xl" data-id="element-227">{tomo.icon}</span>
                  <div data-id="element-228">
                    <h3 className="text-xl md:text-2xl font-serif font-bold text-white leading-tight mb-2" data-id="element-229">
                      {tomo.title}
                    </h3>
                    <p className="text-gray-300 text-sm md:text-base" data-id="element-230">
                      {tomo.description}
                    </p>
                  </div>
                </div>

                <ul className="space-y-3 mt-6 border-t border-white/10 pt-6" data-id="element-231">
                  {tomo.items.map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-232">
                      <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-233" />
                      <span className="text-gray-200 text-sm md:text-base" data-id="element-234">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>)}
        </div>

        <div className="mt-10 md:mt-16 text-center" data-id="element-235">
          <a href="Link de tu checkout" className="group relative inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 text-base md:text-lg font-bold text-navy bg-gold rounded-lg overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(201,168,76,0.4)]" data-id="element-236">
            <span className="relative z-10 flex items-center" data-id="element-237">
              👉 Acceder al Sistema PRO ahora
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-238" />
            </span>
            <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-239"></div>
          </a>
        </div>
      </div>
    </section>;
}